import classNames from "classnames";
import { CustomValuesList } from "PFApp/components/custom_values_list/custom_values_list";
import { Accordion } from "PFComponents/accordion";
import { Typography } from "PFComponents/typography";
import { useReadableDuration } from "PFCore/helpers/use_readable_duration";
import { Activity, CustomType, Profile } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ExperienceDescription } from "./experience_description";
import css from "./experience_section.module.scss";
import { InternalExperienceFeedback } from "./internal_experience_feedback";
import { InternalExperienceEditButton } from "./parts/internal_experience_edit_button";

type InternalExperienceProps = {
  experience: Activity;
  customTypes: CustomType[];
  isCfPermitted: (cfName: string) => boolean;
  fetchInternalExperience: () => void;
  profile: Profile;
};

type InfoSectionProps = {
  label: string;
  content: React.ReactNode | string;
};

export const InfoSection = ({ label, content }: InfoSectionProps) => (
  <div key={label} className={css.experienceGrid}>
    <Typography variant="bodyButtons" clipOverflow>
      {label}
    </Typography>
    <Typography variant="bodyRegular">{content}</Typography>
  </div>
);

export const InternalExperience = ({
  experience,
  customTypes,
  isCfPermitted,
  fetchInternalExperience,
  profile
}: InternalExperienceProps) => {
  const { t } = useTranslation(["profiles", "translation"]);

  const readableDuration = useReadableDuration({
    duration: experience.metadata.date_range
  });

  const customFields = useMemo(() => experience.custom_fields || [], [experience.custom_fields]);
  const experienceCustomFields = useMemo(() => {
    const experienceCustomFields = {};

    customFields.forEach((customField) => {
      const customType = (customTypes || []).find((customType) => customType.name === customField.type.name);
      if (customType?.name) {
        experienceCustomFields[customType.name] = {
          type: customType,
          values: customField.values
        };
      }
    });
    return experienceCustomFields;
  }, [customTypes, customFields]);

  const allCustomFields = useMemo(() => Object.keys(experienceCustomFields), [experienceCustomFields]);

  return (
    <div
      className={classNames(css.row, css.internalPositionDetails)}
      key={`${experience.id}`}
      data-scroll-anchor={`internal-experience-${experience.id}`}
    >
      <div className={classNames(css.experienceTitle)}>
        <Typography variant="h5">{readableDuration}</Typography>
      </div>
      <div className={css.internalPositionAccordion}>
        <Accordion
          animated={false}
          headerVariant="h5"
          header={`${t("translation:role")}: ${experience.name}`}
          defaultState="closed"
        >
          <div className={classNames(css.experienceGridWrapper)}>
            <InfoSection label={t("translation:role")} content={experience.name} />
            {allCustomFields.map(
              (name) =>
                isCfPermitted(name) && (
                  <div key={name} className={css.experienceGrid}>
                    <Typography variant="bodyBold" clipOverflow>
                      {experienceCustomFields[name].type.display_as}
                    </Typography>
                    <div>
                      <CustomValuesList customValues={experienceCustomFields[name].values} limit={6} />
                    </div>
                  </div>
                )
            )}
            {experience.metadata.location && (
              <InfoSection label={t("profiles:show.parts.location")} content={experience.metadata.location} />
            )}
            <InfoSection
              label={t("profiles:show.parts.description")}
              content={<ExperienceDescription content={experience.description} maxLength={100} />}
            />
            {experience.metadata.engagement_hours && (
              <InfoSection
                label={t("profiles:show.parts.hours")}
                content={experience.metadata.engagement_hours}
              />
            )}
            {experience.parent_activity_id && (
              <InternalExperienceFeedback roleId={experience.parent_activity_id} profileId={profile.id} />
            )}
          </div>
        </Accordion>
        <InternalExperienceEditButton
          experience={experience}
          profile={profile}
          fetchInternalExperience={fetchInternalExperience}
        />
      </div>
    </div>
  );
};
